import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ReferenceLine,
} from "recharts";
import { AlertCircle, Loader2, Plus } from "lucide-react";
import {
  ACCENT, NIHSS_ITEM_DEFS, SEV_COLOR, fmtDate, Field, SeverityBadge, HistoryTable,
} from "./shared";

export default function NihssTab({ data, onSaved }) {
  const [items, setItems] = useState(() => {
    const init = {};
    NIHSS_ITEM_DEFS.forEach(([k]) => { init[k] = 0; });
    return init;
  });
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const runningTotal = useMemo(
    () => Object.values(items).reduce((a, b) => a + (Number(b) || 0), 0),
    [items]
  );

  async function livePreview(next) {
    try {
      const { data: r } = await api.post("/specialties/neurology/scores/nihss", { items: next });
      setPreview(r);
    } catch { /* ignore */ }
  }

  function setItem(key, val) {
    const next = { ...items, [key]: Number(val) };
    setItems(next);
    livePreview(next);
  }

  async function submit(e) {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      await api.post("/specialties/neurology/nihss", { date, items, notes: notes || null });
      toast.success(`NIHSS ${runningTotal} recorded`);
      const zero = {}; NIHSS_ITEM_DEFS.forEach(([k]) => { zero[k] = 0; });
      setItems(zero); setPreview(null); setNotes("");
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save NIHSS");
    } finally { setSaving(false); }
  }

  const chartData = useMemo(
    () => [...data.records].slice().reverse().map((r) => ({ date: fmtDate(r.date), NIHSS: r.total })),
    [data.records]
  );

  return (
    <div className="space-y-6" data-testid="neuro-nihss-tab">
      <form onSubmit={submit} className="ros-card space-y-4" data-testid="nihss-form">
        <div className="flex items-center justify-between">
          <div className="font-heading text-lg">Score NIH Stroke Scale</div>
          <div className="flex items-baseline gap-4">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Running total</div>
              <div className="text-3xl font-heading font-bold" style={{ color: ACCENT }} data-testid="nihss-running-total">
                {runningTotal}<span className="text-sm text-[#A3ACA7]">/ 42</span>
              </div>
            </div>
            {preview && <SeverityBadge value={preview.severity} />}
          </div>
        </div>

        <Field label="Assessment date">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                 data-testid="nihss-input-date"
                 className="ros-input" required />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {NIHSS_ITEM_DEFS.map(([key, label, cap, opts]) => (
            <div key={key} className="ros-card-raised" data-testid={`nihss-item-${key}`}>
              <div className="text-xs font-semibold text-white">{label}</div>
              <div className="text-[10px] text-[#A3ACA7] mt-0.5">Max score {cap}</div>
              <select value={items[key]}
                      onChange={(e) => setItem(key, e.target.value)}
                      data-testid={`nihss-select-${key}`}
                      className="ros-input mt-2">
                {opts.map((label2, idx) => (
                  <option key={idx} value={idx}>{label2}</option>
                ))}
              </select>
            </div>
          ))}
        </div>

        <Field label="Notes">
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                    data-testid="nihss-input-notes"
                    className="ros-input" placeholder="Time, reperfusion decision, imaging findings…" />
        </Field>

        {preview?.recommendation && (
          <div className="text-xs p-3 rounded-xl border"
               style={{ background: `${SEV_COLOR[preview.severity] || "#A3ACA7"}12`,
                        borderColor: `${SEV_COLOR[preview.severity] || "#A3ACA7"}40` }}
               data-testid="nihss-recommendation">
            <span className="font-semibold text-white">{preview.severity}:</span> {preview.recommendation}
          </div>
        )}

        {error && (
          <div className="text-sm text-[#F87171] flex items-center gap-2" data-testid="nihss-error">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div>
          <button type="submit" disabled={saving}
                  data-testid="nihss-submit"
                  className="flex items-center gap-2 px-5 py-2 rounded-full text-black font-semibold disabled:opacity-50 transition-colors"
                  style={{ background: ACCENT }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {saving ? "Saving…" : "Save NIHSS assessment"}
          </button>
        </div>
      </form>

      {chartData.length > 0 && (
        <div className="ros-card" data-testid="nihss-chart">
          <div className="font-heading text-lg mb-3">NIHSS trend</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 16, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#272E2B" />
                <XAxis dataKey="date" stroke="#A3ACA7" fontSize={11} />
                <YAxis stroke="#A3ACA7" fontSize={11} domain={[0, 42]} />
                <Tooltip contentStyle={{ background: "#0F1312", border: "1px solid #272E2B", borderRadius: 12 }} />
                <ReferenceLine y={4}  stroke="#FBBF24" strokeDasharray="4 4" label={{ value: "Minor ≤4", fontSize: 10, fill: "#FBBF24" }} />
                <ReferenceLine y={15} stroke="#FB923C" strokeDasharray="4 4" label={{ value: "Moderate ≤15", fontSize: 10, fill: "#FB923C" }} />
                <ReferenceLine y={20} stroke="#F87171" strokeDasharray="4 4" label={{ value: "Mod-severe ≤20", fontSize: 10, fill: "#F87171" }} />
                <Line type="monotone" dataKey="NIHSS" stroke={ACCENT} strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <HistoryTable
        title={`NIHSS history (${data.records.length})`}
        testId="nihss-history"
        rows={data.records}
        rowTestId={(r) => `nihss-row-${r.id}`}
        empty="No NIHSS records yet."
        columns={[
          { label: "Date", render: (r) => fmtDate(r.date) },
          { label: "Total", render: (r) => <span className="font-mono">{r.total}</span> },
          { label: "Severity", render: (r) => <SeverityBadge value={r.severity} /> },
          { label: "Notes", render: (r) => <span className="text-[#A3ACA7] text-xs">{r.notes || ""}</span> },
        ]}
      />
    </div>
  );
}
